import { UseFetchOptions, UseFetchReturn } from '@vueuse/core';
import { watchImmediate } from '@vueuse/shared';
import { JsonApiDocument, ModelForType } from 'json-api-models';
import {
    computed,
    ComputedRef,
    markRaw,
    MaybeRef,
    MaybeRefOrGetter,
    Raw,
    Ref,
    ref,
    ShallowRef,
    shallowRef,
    unref,
} from 'vue';

import { Models, models } from '../../models.ts';
import { hasIncluded } from '../../utils/included.ts';
import { handleReauthentication, useApiRequestBase } from './transport.ts';

const requestMethods = ['get', 'post', 'put', 'delete', 'patch', 'head', 'options'] as const;
const responseMethods = ['json', 'text', 'blob', 'arrayBuffer', 'formData'] as const;
type RequestMethod = (typeof requestMethods)[number];
type ResponseMethod = (typeof responseMethods)[number];
type ApiRequestPromise<T, Data = JsonApiDocument> = UseApiRequestReturn<T, Data> &
    PromiseLike<UseApiRequestReturn<T, Data>>;

export type UseApiRequestReturn<T, Data = JsonApiDocument> = Omit<
    UseFetchReturn<Data>,
    RequestMethod | ResponseMethod
> & {
    model: ShallowRef<T | null>;
    sync: Ref<boolean>;
    json: <JSON = JsonApiDocument>() => ApiRequestPromise<T, JSON>;
    text: () => ApiRequestPromise<T, string>;
    blob: () => ApiRequestPromise<T, Blob>;
    arrayBuffer: () => ApiRequestPromise<T, ArrayBuffer>;
    formData: () => ApiRequestPromise<T, FormData>;
} & {
    [Method in RequestMethod]: (
        ...args: Parameters<UseFetchReturn<Data>[Method]>
    ) => ApiRequestPromise<T, Data>;
};

export function useApiRequest<T = any>(
    url: MaybeRefOrGetter<string>,
    useFetchOptions?: UseFetchOptions,
): ApiRequestPromise<T>;
export function useApiRequest<T = any>(
    url: MaybeRefOrGetter<string>,
    options: RequestInit,
    useFetchOptions?: UseFetchOptions,
): ApiRequestPromise<T>;

export function useApiRequest<T = any>(...args: any[]) {
    const request = useApiRequestBase(
        ...(args as Parameters<typeof useApiRequestBase>),
    ).json<JsonApiDocument>();

    handleReauthentication(request);

    const model = shallowRef<T | null>(null);
    const sync = ref(true);

    request.onFetchResponse(() => {
        const data = request.data.value;
        if (sync.value) {
            model.value = data ? (models.sync(data) as T | null) : null;
        }
    });

    request.onFetchError(() => {
        model.value = null;
    });

    const result = { ...request, model, sync } as ApiRequestPromise<T>;

    // VueUse's fluent methods share request state but return new objects.
    // Keep our model and sync refs on every returned request.
    for (const method of [...requestMethods, ...responseMethods]) {
        const configure = request[method] as (...args: unknown[]) => unknown;
        Object.assign(result, {
            [method]: (...args: unknown[]) => (configure(...args) ? result : undefined),
        });
    }

    // oxlint-disable-next-line no-thenable
    result.then = (onFulfilled, onRejected) =>
        request
            // Await must resolve to an object without a then method.
            // oxlint-disable-next-line no-thenable
            .then(() => ({ ...result, then: undefined }))
            .then(onFulfilled, onRejected);

    return result;
}

export interface UseModelReturn<T extends string> {
    model: ComputedRef<ModelForType<T, Models> | null>;
    request: ShallowRef<Raw<UseApiRequestReturn<ModelForType<T, Models>>>>;
}

export function useModel<T extends string>(
    type: MaybeRef<T>,
    id: MaybeRef<string | null | undefined>,
    include: MaybeRef<string[]> = [],
    query?: MaybeRef<Record<string, string>>,
    options: { skipIfLoaded?: boolean } = {},
): UseModelReturn<T> {
    const model = computed(() => {
        const idValue = unref(id);
        const typeValue = unref(type);
        if (!typeValue || !idValue) return null;
        return models.find(typeValue, idValue);
    });

    const request = shallowRef();

    const url = computed(() => {
        const typeValue = unref(type);
        const idValue = unref(id);
        if (!typeValue || !idValue) return '';
        const i = unref(include);
        const q = i ? new URLSearchParams({ include: i.join(','), ...unref(query) }) : null;
        return `${typeValue}/${idValue}` + (q ? '?' + q : '');
    });

    // Models are refetched each time they're shown, unless the caller opts to
    // skip that when the store already has everything included.
    watchImmediate(url, () => {
        const loaded =
            options.skipIfLoaded && model.value && hasIncluded(model.value, unref(include));
        request.value = markRaw(useApiRequest(url.value, { immediate: !!url.value && !loaded }));
    });

    return { model, request };
}
